/**
 * DNACORE V4 — Design System Showcase
 * Página de demonstração visual de todos os tokens + primitivas.
 * Rota: /v4/design
 */
import React, { useState } from 'react';
import { Activity, AlertTriangle, CheckCircle, Search, Trash2, Plus } from 'lucide-react';
import {
  DnaCard, DnaButton, DnaBadge, DnaInput, DnaTextarea, DnaSelect,
  DnaModal, DnaDrawer, DnaSkeleton, DnaEmptyState, DnaHelixBg, DnaNeuralBg, DnaSection,
  DnaTableV4,
} from '../design-system/DnaDesignSystem';
import DnaHeaderV4 from '../design-system/DnaHeaderV4';

export default function DnaDesignShowcase() {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <DnaNeuralBg className="min-h-screen" style={{ background: 'var(--dna-bg)' }}>
      <DnaHeaderV4
        title="Design System"
        subtitle="Tokens, primitivas e identidade visual unificada"
        actions={<>
          <DnaButton variant="ghost" size="sm">Docs</DnaButton>
          <DnaButton variant="primary" size="sm">Compartilhar</DnaButton>
        </>}
      />
      <DnaHelixBg>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'var(--dna-space-7) var(--dna-space-5)' }}>
          <div style={{ marginBottom: 32 }}>
            <DnaBadge variant="accent">DNACore V4 · Onda 1</DnaBadge>
            <h1 style={{ fontSize: 44, fontWeight: 900, letterSpacing: '-0.02em', color: 'var(--dna-text)', marginTop: 12 }}>
              The Operating System for Education
            </h1>
            <p style={{ color: 'var(--dna-text-muted)', fontSize: 16, marginTop: 6, maxWidth: 720 }}>
              Design System Foundation — tokens, primitivas e identidade visual unificada.
              Importe de <code style={{ color: 'var(--dna-accent)' }}>@/design-system/DnaDesignSystem</code>.
            </p>
          </div>

          <DnaSection title="Botões" sub="primary · secondary · ghost · success · danger">
            <DnaCard>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <DnaButton variant="primary" icon={Plus}>Novo Aluno</DnaButton>
                <DnaButton variant="secondary">Cancelar</DnaButton>
                <DnaButton variant="ghost">Ação leve</DnaButton>
                <DnaButton variant="success" icon={CheckCircle}>Confirmar</DnaButton>
                <DnaButton variant="danger" icon={Trash2}>Excluir</DnaButton>
                <DnaButton variant="primary" pill size="lg">Pill grande</DnaButton>
                <DnaButton variant="secondary" size="sm">Small</DnaButton>
              </div>
            </DnaCard>
          </DnaSection>

          <DnaSection title="Badges" sub="status · severity · IA">
            <DnaCard>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <DnaBadge>Default</DnaBadge>
                <DnaBadge variant="success">Sucesso</DnaBadge>
                <DnaBadge variant="warn">Atenção</DnaBadge>
                <DnaBadge variant="danger">Crítico</DnaBadge>
                <DnaBadge variant="info">Info</DnaBadge>
                <DnaBadge variant="accent">DNA Ativo</DnaBadge>
                <DnaBadge variant="ai">Análise IA</DnaBadge>
              </div>
            </DnaCard>
          </DnaSection>

          <DnaSection title="Cards" sub="surface · interactive · glass · accent">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
              <DnaCard title="Card padrão">Conteúdo neutro.</DnaCard>
              <DnaCard title="Interactive" interactive>Hover para sentir o lift.</DnaCard>
              <DnaCard title="Glass blur" glass>Backdrop blur 18px + saturate.</DnaCard>
              <DnaCard title="Accent" accent>Glow rose do DNA.</DnaCard>
            </div>
          </DnaSection>

          <DnaSection title="Inputs" sub="forms padronizados">
            <DnaCard>
              <DnaInput label="Nome do Aluno" placeholder="Ex.: Maria da Silva" testId="ds-input-name" />
              <DnaSelect label="Segmento" options={['Educação Infantil', 'Fundamental I', 'Fundamental II', 'Ensino Médio']} testId="ds-select" />
              <DnaTextarea label="Observações" placeholder="Notas pedagógicas…" rows={3} testId="ds-textarea" />
            </DnaCard>
          </DnaSection>

          <DnaSection title="Overlays" sub="Modal · Drawer">
            <DnaCard>
              <div style={{ display: 'flex', gap: 8 }}>
                <DnaButton onClick={() => setModalOpen(true)} testId="ds-open-modal">Abrir Modal</DnaButton>
                <DnaButton variant="secondary" onClick={() => setDrawerOpen(true)} testId="ds-open-drawer">Abrir Drawer</DnaButton>
              </div>
            </DnaCard>
          </DnaSection>

          <DnaSection title="Loading & Empty" sub="estados de transição">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <DnaCard title="Skeleton">
                <DnaSkeleton height={20} style={{ marginBottom: 10 }} />
                <DnaSkeleton height={20} width="80%" style={{ marginBottom: 10 }} />
                <DnaSkeleton height={20} width="60%" />
              </DnaCard>
              <DnaCard>
                <DnaEmptyState
                  icon={Search}
                  title="Nenhum resultado"
                  sub="Tente refinar os filtros ou pesquisar por outro termo."
                  action={<DnaButton variant="secondary" size="sm">Limpar filtros</DnaButton>}
                />
              </DnaCard>
            </div>
          </DnaSection>

          <DnaSection title="Tokens vivos" sub="aplique como variáveis CSS em qualquer componente">
            <DnaCard>
              <code style={{ fontSize: 13, color: 'var(--dna-text-muted)', display: 'block', lineHeight: 1.9 }}>
                --dna-accent: <span style={{ color: 'var(--dna-accent)' }}>#ff3b6b</span>{'  ·  '}
                --dna-accent-2: <span style={{ color: 'var(--dna-accent-2)' }}>#fbbf24</span>{'  ·  '}
                --dna-success: <span style={{ color: 'var(--dna-success)' }}>#22c55e</span>{'  ·  '}
                --dna-info: <span style={{ color: 'var(--dna-info)' }}>#06b6d4</span><br />
                --dna-radius-lg: 16px · --dna-motion: 200ms · --dna-ease: cubic-bezier(0.2, 0.7, 0.2, 1)
              </code>
            </DnaCard>
          </DnaSection>

          <DnaSection title="DnaTableV4" sub="Onda 3 — wrapper premium para tabelas legacy (sticky header, density, zebra)">
            <DnaTableV4
              title="Títulos em aberto"
              subtitle="Exemplo · 4 registros"
              stickyHeader
              zebra
              theme="dark"
              maxHeight="320px"
              actions={<>
                <DnaButton size="sm" variant="ghost">Exportar</DnaButton>
                <DnaButton size="sm" variant="primary" icon={Plus}>Novo</DnaButton>
              </>}
              testId="showcase-table-v4"
            >
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Aluno</th>
                    <th style={{ textAlign: 'left' }}>Descrição</th>
                    <th className="text-right" style={{ textAlign: 'right' }}>Valor</th>
                    <th style={{ textAlign: 'center' }}>Vencimento</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { aluno: 'Ana Souza', desc: 'Mensalidade 03/2026', valor: 'R$ 1.480,00', venc: '10/03/2026', status: 'pendente' },
                    { aluno: 'Bruno Lima', desc: 'Material didático', valor: 'R$ 320,00', venc: '15/03/2026', status: 'enviado' },
                    { aluno: 'Carla Reis', desc: 'Mensalidade 03/2026', valor: 'R$ 1.480,00', venc: '10/03/2026', status: 'pago' },
                    { aluno: 'Diego Alves', desc: 'Mensalidade 02/2026', valor: 'R$ 1.480,00', venc: '10/02/2026', status: 'atrasado' },
                  ].map((r, i) => (
                    <tr key={i}>
                      <td>{r.aluno}</td>
                      <td>{r.desc}</td>
                      <td className="text-right" style={{ textAlign: 'right' }}>{r.valor}</td>
                      <td style={{ textAlign: 'center' }}>{r.venc}</td>
                      <td style={{ textAlign: 'center' }}>
                        <DnaBadge variant={r.status === 'pago' ? 'success' : r.status === 'atrasado' ? 'danger' : r.status === 'enviado' ? 'info' : 'warn'}>
                          {r.status}
                        </DnaBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DnaTableV4>
          </DnaSection>

          <DnaModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Confirmar Exclusão"
            subtitle="Esta ação não pode ser desfeita."
            footer={<>
              <DnaButton variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</DnaButton>
              <DnaButton variant="danger" icon={Trash2} onClick={() => setModalOpen(false)}>Excluir</DnaButton>
            </>}
          >
            <p style={{ fontSize: 14, lineHeight: 1.6 }}>
              Tem certeza que deseja excluir este item? Todos os dados relacionados serão permanentemente removidos.
            </p>
          </DnaModal>

          <DnaDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Filtros Avançados">
            <DnaInput label="Buscar" placeholder="Nome ou RA" />
            <DnaSelect label="Status" options={['Ativos', 'Inativos', 'Todos']} />
            <DnaSelect label="Turma" options={['6º ANO A', '7º ANO A', '8º ANO A']} />
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <DnaButton variant="ghost" onClick={() => setDrawerOpen(false)}>Limpar</DnaButton>
              <DnaButton variant="primary" onClick={() => setDrawerOpen(false)} icon={Activity}>Aplicar</DnaButton>
            </div>
          </DnaDrawer>
        </div>
      </DnaHelixBg>
    </DnaNeuralBg>
  );
}
